import { CommonModule } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
	ChangeDetectionStrategy,
	Component,
	effect,
	inject,
	input,
	model,
	output,
	signal,
} from '@angular/core';

import { LetDirective } from '@ngrx/component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import { LabelPosition } from '@valk-nx/components/ui-label/src/lib/label.interface';
import { LabelComponent } from '@valk-nx/components/ui-label/src/lib/label/label';
import { StepperComponent } from '@valk-nx/components/ui-stepper/src/lib/stepper';
import type { OccupancyRestrictionsInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';
import type { OccupancyInterface } from '@valk-nx/helpers/lib/interfaces/occupancy.interface';
import { OccupancyHelper } from '@valk-nx/helpers/lib/occupancy/occupancy.helper';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

import { AvailabilityGroupBookingNoticeComponent } from '../availability-group-booking-notice/availability-group-booking-notice';
import { AvailabilityWidgetInputComponent } from '../availability-input/availability-input';

import type {
	OccupancyRange,
	OccupancyTypes,
} from './availability-input-occupancy.types';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-availability-widget-input-occupancy`,
	templateUrl: './availability-input-occupancy.html',
	imports: [
		AngularSvgIconModule,
		AvailabilityGroupBookingNoticeComponent,
		AvailabilityWidgetInputComponent,
		CommonModule,
		LabelComponent,
		StepperComponent,
		LetDirective,
		TranslatePipe,
	],
})
export class AvailabilityWidgetInputOccupancyComponent
	implements OnInit, OnChanges
{
	private readonly translate = inject(TranslateService);
	private readonly viewport = inject(ViewPortService);

	className = input<string>('');
	maxRooms = input<number>(5);
	defaultAdults = input<number>(2);
	initialOccupancy = input<OccupancyInterface[]>([
		{ adults: 2, children: 0, infants: 0 },
	]);
	ctaLabel = input<string>('global.choose-dates');
	isGroupBooking = input<boolean>(false);
	contactUrl = input<string>('');
	isOpen = model<boolean>();

	occupancyRestrictions = input<OccupancyRestrictionsInterface>({
		adults: {
			min: 1,
			max: 3,
		},
		children: {
			min: 0,
			max: 1,
		},
		infants: {
			min: 0,
			max: 1,
		},
		maxPersons: 2,
		maxOccupancies: 5,
	});

	occupancyChanged = output<OccupancyInterface[]>();
	emitCta = output<Event>();
	emitBackButton = output<Event>();

	isSmallTablet$: Observable<boolean>;
	occupancy = signal<OccupancyInterface[]>([]);

	position$: Observable<LabelPosition>;

	constructor() {
		this.isSmallTablet$ = this.viewport.isSmallTablet$;
		this.position$ = this.viewport.isMobile$.pipe(
			map((isMobile) =>
				isMobile ? LabelPosition.SpaceBetween : LabelPosition.TopAlign,
			),
		);
		effect(() => {
			this.validateCurrentOccupancy(
				this.occupancyRestrictions(),
				this.isGroupBooking(),
			);
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			changes['initialOccupancy'] &&
			!changes['initialOccupancy'].firstChange
		) {
			this.setOccupancyFromInitial();
		}
	}

	ngOnInit(): void {
		this.setOccupancyFromInitial();
	}

	private setOccupancyFromInitial(): void {
		if (
			this.initialOccupancy() &&
			this.initialOccupancy().length <= this.maxRooms()
		) {
			const { adults, children, infants } = this.occupancyRestrictions();
			this.occupancy.set(
				this.initialOccupancy().map((occupancy) => {
					return {
						adults: this.limitOccupancy(occupancy.adults, adults),
						children: this.limitOccupancy(
							occupancy.children,
							children,
						),
						infants: this.limitOccupancy(
							occupancy.infants,
							infants,
						),
					};
				}),
			);
		} else {
			this.occupancy.set([this.createRoomOccupancy()]);
		}

		// only emit if anything changed (initialOccupancy != occupancy)
		if (!isOccupancyEqual(this.initialOccupancy(), this.occupancy())) {
			// occupancy has been either newly created or has been restricted using the occupancy restrictions
			this.occupancyChanged.emit(this.occupancy());
		}
	}

	private validateCurrentOccupancy(
		occupancyRestrictions: OccupancyRestrictionsInterface,
		isGroupBooking: boolean,
	): void {
		const { adults, children, infants, maxOccupancies } =
			occupancyRestrictions;

		const limit = (value: number, min: number, max: number): number =>
			Math.min(Math.max(value, min), max);

		const truncatedOccupancy = OccupancyHelper.enforceCurrentOccupancyLimit(
			this.occupancy(),
			maxOccupancies ?? this.maxRooms(),
		);

		const newOccupancy = truncatedOccupancy.map((room) => {
			const newAdults = limit(room.adults, adults.min, adults.max);
			// Shiji does not support children or infants in group reservations
			const newChildren = isGroupBooking
				? 0
				: limit(room.children, children.min, children.max);
			const newInfants = isGroupBooking
				? 0
				: limit(room.infants, infants.min, infants.max);

			return OccupancyHelper.enforceMaxPersonsLimit(
				{
					adults: newAdults,
					children: newChildren,
					infants: newInfants,
				},
				this.occupancyRestrictions(),
			);
		});

		if (!isOccupancyEqual(this.occupancy(), newOccupancy)) {
			this.occupancy.set(newOccupancy);
			this.occupancyChanged.emit(this.occupancy());
		}
	}

	occupancyValueChanged(value: number, type: OccupancyTypes, index: number) {
		this.occupancy.set(
			this.occupancy().map((room, i) =>
				i === index ? { ...room, [type]: +value } : room,
			),
		);
		this.occupancyChanged.emit(this.occupancy());
	}

	onRemoveRoom(event: Event, index: number) {
		this.occupancy.set([
			...this.occupancy().slice(0, index),
			...this.occupancy().slice(index + 1),
		]);
		this.occupancyChanged.emit(this.occupancy());
		event.stopPropagation();
	}

	onAddRoom(event: Event) {
		this.occupancy.set([...this.occupancy(), this.createRoomOccupancy()]);
		this.occupancyChanged.emit(this.occupancy());
		event.stopPropagation();
	}

	maxOccupancy(
		occupancyValues: { adults: number; infants: number; children: number },
		occupancyType: string,
	): number {
		if (
			occupancyValues.adults +
				occupancyValues.infants +
				occupancyValues.children >=
			this.occupancyRestrictions().maxPersons
		) {
			return occupancyValues[occupancyType];
		}
		return this.occupancyRestrictions()[occupancyType].max;
	}

	get roomLabel(): string {
		const translationKey =
			this.occupancy().length === 1
				? 'global.room-inline'
				: 'global.rooms-inline';

		return `${this.occupancy().length} ${this.translate.instant(
			translationKey,
		)}`;
	}

	get personLabel(): string {
		const sum = (totalSoFar: number, value: number) => totalSoFar + value;

		const amountOfPersons = this.occupancy()
			.map((room) => Object.values(room).reduce(sum, 0))
			.reduce(sum, 0);

		const translationKey =
			amountOfPersons === 1 ? 'global.person' : 'global.persons';

		return `${amountOfPersons} ${this.translate.instant(translationKey)}`;
	}

	private limitOccupancy(
		numOccupants: number,
		occupancyRange: OccupancyRange,
	) {
		return Math.min(
			Math.max(numOccupants, occupancyRange.min),
			occupancyRange.max,
		);
	}

	private createRoomOccupancy(): OccupancyInterface {
		const { adults, children, infants } = this.occupancyRestrictions();
		const defaultAdults = this.defaultAdults();

		const limit = (value: number, min: number, max: number): number =>
			Math.min(Math.max(value, min), max);
		return {
			adults: limit(
				adults.default ?? defaultAdults,
				adults.min,
				adults.max,
			),
			children: limit(children.default ?? 0, children.min, children.max),
			infants: limit(infants.default ?? 0, infants.min, infants.max),
		};
	}

	onEmitBackButton(event: Event) {
		this.isOpen.set(false);
		this.emitBackButton.emit(event);
	}
}

export function isOccupancyEqual(
	a: OccupancyInterface[] | undefined,
	b: OccupancyInterface[] | undefined,
): boolean {
	if (!a || !b) return false;
	if (a.length !== b.length) return false;
	return a.every(
		(room, index) =>
			room.adults === b[index].adults &&
			room.children === b[index].children &&
			room.infants === b[index].infants,
	);
}
